import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { usePermission } from "../../hooks/usePermission";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { formatKey, getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import { PrimaryButton } from "../../globalComponents/ui/Button";
import TagFilterBar from "./Components/TagFilterBar";
import { parseTags, questionMatchesSteps, getNextEligibleFilters, formatTagKey } from "./Components/TagFilterUtils";
import Spinner from "../../globalComponents/ui/Spinner";
import Checkbox from "../../globalComponents/ui/Checkbox";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";


function normaliseDb(raw) {
  const dv = raw?.dataValues ?? raw;
  return { id: dv.id, name: dv.name, source: dv.source };
}



// ─── DatabasePicker ───────────────────────────────────────────────────────────
function DatabasePicker({ databases, value, onChange, compact = false, disabled = false }) {
  if (!databases || databases.length === 0) {
    return <span style={{ fontSize: 10, color: "#cbd5e1", fontStyle: "italic" }}>no DBs</span>;
  }
  const hasValue = !!value;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3 }} onClick={e => e.stopPropagation()}>
      <select
        value={value ?? ""}
        disabled={disabled}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        style={{
          padding: compact ? "3px 6px" : "5px 10px",
          borderRadius: 6,
          border: `1.5px solid ${hasValue ? "#fcd34d" : "#e2e8f0"}`,
          fontSize: compact ? 10 : 11,
          color: hasValue ? "#d97706" : "#94a3b8",
          background: hasValue ? "#fffbeb" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          maxWidth: compact ? 130 : 190,
          fontWeight: hasValue ? 600 : 400,
          outline: "none",
          appearance: "none",
          paddingRight: compact ? 16 : 20,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 5px center",
          backgroundSize: "7px"
        }}
      >
        <option value="">Assign DB…</option>
        {databases.map(db => (
          <option key={db.id} value={db.id}>{db.name}</option>
        ))}
      </select>
      {hasValue && !disabled && (
        <button
          onClick={e => { e.stopPropagation(); onChange(null); }}
          style={{ fontSize: 12, border: "none", background: "none", color: "#cbd5e1", cursor: "pointer", padding: "0 2px", fontWeight: 900, lineHeight: 1 }}
          onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
          onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}
        >×</button>
      )}
    </div>
  );
}

// ─── Filter DB Assign Bar ─────────────────────────────────────────────────────
function FilterDbAssignBar({ databases, filterSteps, allQuestions, kpiDbMap, onKpiDbChange, disabled }) {
  const matchingKpis = useMemo(() => {
    const active = filterSteps.filter(s => s.values?.size);
    if (!active.length) return [];
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [allQuestions, filterSteps]);

  const dbMeta = useMemo(() => {
    if (!matchingKpis.length) return { assigned: 0, total: 0, value: null, isMixed: false };
    const ids = matchingKpis.map(q => kpiDbMap[q.questionId] || null);
    const assigned = ids.filter(Boolean).length;
    const unique = new Set(ids.filter(Boolean));
    const isMixed = unique.size > 1 || (assigned > 0 && assigned < matchingKpis.length);
    const value = (unique.size === 1 && assigned === matchingKpis.length) ? [...unique][0] : null;
    return { assigned, total: matchingKpis.length, value, isMixed };
  }, [matchingKpis, kpiDbMap]);

  const activeSteps = filterSteps.filter(s => s.values?.size);
  if (!activeSteps.length || !databases.length || !matchingKpis.length) return null;

  return (
    <div style={{
      marginTop: 10, padding: "8px 14px", borderRadius: 10,
      background: dbMeta.value ? "#fffbeb" : "#f8fafc",
      border: `1.5px solid ${dbMeta.value ? "#fcd34d" : "#e2e8f0"}`,
      display: "flex", alignItems: "center", justifyContent: "space-between"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>🗄️</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>Assign DB to filtered KPIs</div>
          <div style={{ fontSize: 10, color: "#b45309" }}>Updates all {matchingKpis.length} KPIs matching these filters</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {dbMeta.isMixed && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#ea580c", background: "#fff7ed", padding: "2px 6px", borderRadius: 4, border: "1px solid #fdba74" }}>
            Mixed ({dbMeta.assigned}/{dbMeta.total})
          </span>
        )}
        <DatabasePicker
          databases={databases}
          value={dbMeta.value}
          disabled={disabled}
          onChange={dbId => matchingKpis.forEach(q => onKpiDbChange(q.questionId, dbId))}
        />
      </div>
    </div>
  );
}



// ─── DB Coverage Legend ───────────────────────────────────────────────────────
function DbLegend({ databases, kpiDbMap, pendingIds }) {
  const dbUsage = useMemo(() => {
    const map = new Map();
    for (const id of pendingIds) {
      const dbId = kpiDbMap[id];
      if (!dbId) continue;
      map.set(dbId, (map.get(dbId) || 0) + 1);
    }
    return map;
  }, [pendingIds, kpiDbMap]);

  const unassignedCount = useMemo(() =>
    Array.from(pendingIds).filter(id => !kpiDbMap[id]).length,
    [pendingIds, kpiDbMap]
  );

  if (dbUsage.size === 0 && unassignedCount === 0) return null;

  const normDbs = (databases || []).map(normaliseDb);
  return (
    <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "10px 16px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px" }}>🗄 DB Coverage:</span>
      {Array.from(dbUsage.entries()).map(([dbId, count]) => {
        const db = normDbs.find(d => d.id === dbId);
        return (
          <span key={dbId} style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fff", border: "1px solid #fcd34d", color: "#92400e" }}>
            {db?.name || `DB #${dbId}`} · <strong>{count}</strong> KPI{count !== 1 ? "s" : ""}
          </span>
        );
      })}
      {unassignedCount > 0 && (
        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#94a3b8" }}>
          No DB · <strong>{unassignedCount}</strong> KPI{unassignedCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

// ─── FilterBar ────────────────────────────────────────────────────────────────
function FilterBar() { return null; }
function UnusedFilterBar({
  allQuestions, filterSteps, onFilterStepsChange,
  pendingIds, disabled,
  databases, kpiDbMap, onKpiDbChange,
  onSelectFiltered
}) {
  const [openKey, setOpenKey] = useState(null);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { setDropdownSearch(""); }, [openKey]);

  const activeSteps = filterSteps.filter(s => s.values?.size);

  const eligibleNextFilters = useMemo(() => getNextEligibleFilters(allQuestions, filterSteps), [allQuestions, filterSteps]);
  const nextKeys = useMemo(() => {
    const activeKeys = new Set(activeSteps.map(s => s.key));
    return eligibleNextFilters.filter(k => !activeKeys.has(k.key)).map(k => k.key);
  }, [eligibleNextFilters, activeSteps]);

  const openValues = useMemo(() => {
    if (!openKey) return [];
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;
    const vals = new Set();
    for (const q of allQuestions) {
      for (const combo of parseTags(q.tags)) {
        let pos = 0, matched = true;
        for (const step of stepsPrefix) {
          if (pos >= combo.length || combo[pos].key !== step.key || !step.values.has(combo[pos].value)) { matched = false; break; }
          pos++;
        }
        if (matched && combo[pos]?.key === openKey && combo[pos]?.value) vals.add(combo[pos].value);
      }
    }
    return Array.from(vals).sort();
  }, [openKey, filterSteps, activeSteps, allQuestions]);

  const visibleValues = useMemo(() => {
    if (!dropdownSearch.trim()) return openValues;
    const q = dropdownSearch.toLowerCase();
    return openValues.filter(v => v.toLowerCase().includes(q));
  }, [openValues, dropdownSearch]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find(s => s.key === openKey);
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  const openIsLevelOne = useMemo(() => {
    if (!openKey) return false;
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    return stepIdx === 0 || (stepIdx === -1 && activeSteps.length === 0);
  }, [openKey, activeSteps]);

  const getValueCounts = useCallback((key, value) => {
    const stepIdx = activeSteps.findIndex(s => s.key === key);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;
    const matching = allQuestions.filter(q => {
      for (const combo of parseTags(q.tags)) {
        let pos = 0, matched = true;
        for (const step of stepsPrefix) {
          if (pos >= combo.length || combo[pos].key !== step.key || !step.values.has(combo[pos].value)) { matched = false; break; }
          pos++;
        }
        if (matched && combo[pos]?.key === key && combo[pos]?.value === value) return true;
      }
      return false;
    });
    return { total: matching.length, selected: matching.filter(q => pendingIds?.has(q.questionId)).length };
  }, [allQuestions, activeSteps, pendingIds]);

  const toggleValue = (key, value) => {
    if (disabled) return;
    if (openIsLevelOne) {
      const alreadySelected = openSelectedVals.has(value) && openSelectedVals.size === 1;
      const newSteps = alreadySelected ? [] : [{ key, values: new Set([value]) }];
      onFilterStepsChange(newSteps);
      if (!alreadySelected && onSelectFiltered) onSelectFiltered(newSteps);
      setOpenKey(null);
      return;
    }
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === key);
    if (idx === -1) { next.push({ key, values: new Set([value]) }); }
    else {
      const vals = next[idx].values;
      if (vals.has(value)) vals.delete(value); else vals.add(value);
      if (!vals.size) { next.splice(idx, next.length - idx); setOpenKey(null); }
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next);
  };

  const handleSelectAll = () => {
    if (disabled || openIsLevelOne) return;
    const allSel = visibleValues.every(v => openSelectedVals.has(v));
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === openKey);
    if (allSel) {
      if (idx !== -1) {
        visibleValues.forEach(v => next[idx].values.delete(v));
        if (!next[idx].values.size) next.splice(idx, next.length - idx);
      }
    } else {
      if (idx === -1) next.push({ key: openKey, values: new Set(visibleValues) });
      else visibleValues.forEach(v => next[idx].values.add(v));
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next);
  };

  const removeStep = (key) => {
    if (disabled) return;
    const idx = filterSteps.findIndex(s => s.key === key);
    if (idx >= 0) {
      const newSteps = filterSteps.slice(0, idx);
      onFilterStepsChange(newSteps);
      if (onSelectFiltered) onSelectFiltered(newSteps);
      setOpenKey(null);
    }
  };

  const handleOpenKey = (key) => {
    if (disabled) return;
    setOpenKey(prev => prev === key ? null : key);
  };

  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => openSelectedVals.has(v));

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Filter:</span>

        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;
          const matchCount = allQuestions.filter(q => questionMatchesSteps(q, filterSteps)).length;

          return (
            <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
              {i > 0 && <span style={{ color: "#bae6fd", fontSize: 15, lineHeight: 1 }}>›</span>}
              <button
                onClick={() => handleOpenKey(step.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "5px 8px 5px 10px", borderRadius: 7,
                  border: `1.5px solid ${isOpen ? "#3b82f6" : "#93c5fd"}`,
                  background: isOpen ? "#eff6ff" : "#dbeafe",
                  fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
                  color: "#1d4ed8", transition: "all 0.15s"
                }}
              >
                <span style={{ color: "#3b82f6", fontWeight: 700 }}>{formatKey(step.key)}:</span>
                <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 8, background: "#bfdbfe", color: "#1e40af", marginLeft: 2 }}>{matchCount}</span>
                <span style={{ fontSize: 9, color: "#93c5fd" }}>▾</span>
                {!disabled && (
                  <span
                    onClick={e => { e.stopPropagation(); removeStep(step.key); }}
                    style={{ fontSize: 13, lineHeight: 1, color: "#93c5fd", fontWeight: 900, marginLeft: 1, padding: "0 1px", transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                    onMouseLeave={e => e.currentTarget.style.color = "#93c5fd"}
                  >×</span>
                )}
              </button>
            </span>
          );
        })}

        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={{ color: "#e2e8f0", fontSize: 15, lineHeight: 1 }}>›</span>
        )}

        {nextKeys.map(key => {
          const isOpen = openKey === key;
          return (
            <button key={key} onClick={() => handleOpenKey(key)} style={{
              padding: "5px 12px", borderRadius: 7,
              border: `1.5px solid ${isOpen ? "#93c5fd" : "#e2e8f0"}`,
              background: isOpen ? "#eff6ff" : "#fff",
              fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
              color: isOpen ? "#2563eb" : "#64748b", transition: "all 0.15s"
            }}>
              {formatKey(key)} ▾
            </button>
          );
        })}

        {activeSteps.length > 0 && !disabled && (
          <button onClick={() => { onFilterStepsChange([]); if (onSelectFiltered) onSelectFiltered([]); setOpenKey(null); }}
            style={{ fontSize: 10, color: "#ef4444", border: "none", background: "none", cursor: "pointer", fontWeight: 700, marginLeft: 4 }}>
            ✕ Clear
          </button>
        )}
      </div>

      <FilterDbAssignBar
        databases={databases}
        filterSteps={filterSteps}
        allQuestions={allQuestions}
        kpiDbMap={kpiDbMap}
        onKpiDbChange={onKpiDbChange}
        disabled={disabled}
      />

      {openKey && openValues.length > 0 && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 999,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)", overflow: "hidden",
          width: 300, maxHeight: 360, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "7px 12px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{formatKey(openKey)}</span>
            <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>{openIsLevelOne ? "select one" : "multi-select"}</span>
          </div>

          {!openIsLevelOne && (
            <div style={{ padding: "6px 10px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", pointerEvents: "none" }}>⌕</span>
                <input
                  value={dropdownSearch}
                  onChange={e => setDropdownSearch(e.target.value)}
                  placeholder={`Search ${formatKey(openKey)}…`}
                  style={{
                    width: "100%", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 6,
                    padding: "5px 8px 5px 24px", background: "#fff", color: "#1e293b", outline: "none"
                  }}
                />
                {dropdownSearch && (
                  <span onClick={() => setDropdownSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", cursor: "pointer" }}>×</span>
                )}
              </div>
            </div>
          )}

          {!openIsLevelOne && (
            <div style={{ padding: "5px 12px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "#fafafa" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>
                {openSelectedVals.size} of {openValues.length} selected {dropdownSearch && visibleValues.length !== openValues.length && `(${visibleValues.length} shown)`}
              </span>
              <button onClick={handleSelectAll} style={{ fontSize: 11, fontWeight: 600, color: allVisibleSelected ? "#dc2626" : "#0369a1", background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}>
                {allVisibleSelected ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}

          <div style={{ overflowY: "auto", flex: 1 }}>
            {visibleValues.length === 0 ? (
              <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 11, color: "#94a3b8" }}>No options match your search.</div>
            ) : (
              visibleValues.map(val => {
                const { total, selected } = getValueCounts(openKey, val);
                const isChecked = openSelectedVals.has(val);
                return (
                  <div key={val} onClick={() => toggleValue(openKey, val)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", background: isChecked ? "#f0f9ff" : "transparent", borderBottom: "1px solid #f8fafc", transition: "background 0.1s" }}>
                    {openIsLevelOne ? (
                      <span style={{
                        width: 17, height: 17, borderRadius: "50%", flexShrink: 0,
                        border: `1.5px solid ${isChecked ? "#3f88a5" : "#cbd5e1"}`,
                        background: isChecked ? "#3f88a5" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s", boxShadow: isChecked ? "0 1px 3px #3f88a544" : "none"
                      }}>
                        {isChecked && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", display: "block" }} />}
                      </span>
                    ) : (
                      <Checkbox checked={isChecked} onChange={() => toggleValue(openKey, val)} />
                    )}
                    <span style={{ flex: 1, fontSize: 12, color: "#374151", fontWeight: isChecked ? 600 : 400 }}>{val}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, background: isChecked ? "#dbeafe" : "#f1f5f9",
                      color: isChecked ? "#1d4ed8" : "#64748b", borderRadius: 10, padding: "2px 8px",
                      border: `1.5px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`, whiteSpace: "nowrap"
                    }}>
                      {openIsLevelOne ? total : `${selected}/${total}`}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk DB Assign bar ───────────────────────────────────────────────────────
function BulkDbAssignBar({ databases, visibleQuestions, pendingIds, kpiDbMap, onKpiDbChange, disabled }) {
  const [bulkDb, setBulkDb] = useState("");
  if (disabled || !databases.length || !visibleQuestions.length) return null;

  const selectedVisible = visibleQuestions.filter(q => pendingIds.has(q.questionId));
  const assignedCount = selectedVisible.filter(q => kpiDbMap[q.questionId]).length;

  const applyBulk = (dbId) => {
    setBulkDb(dbId);
    if (!dbId) return;
    selectedVisible.forEach(q => onKpiDbChange(q.questionId, Number(dbId)));
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
      background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, marginBottom: 10
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", whiteSpace: "nowrap" }}>
        🗄 Bulk assign to {selectedVisible.length} selected search results:
      </span>
      <select
        value={bulkDb}
        onChange={e => applyBulk(e.target.value)}
        style={{
          padding: "5px 24px 5px 10px", borderRadius: 7, border: "1.5px solid #93c5fd",
          fontSize: 11, fontWeight: 600, color: "#0369a1", background: "#fff",
          cursor: "pointer", outline: "none", appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 6px center", backgroundSize: "7px"
        }}
      >
        <option value="">Choose a database…</option>
        {databases.map(db => <option key={db.id} value={db.id}>{db.name}</option>)}
      </select>
      {assignedCount > 0 && (
        <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
          {assignedCount}/{selectedVisible.length} already assigned
        </span>
      )}
    </div>
  );
}

// ─── KPI List Row ─────────────────────────────────────────────────────────────
function KpiListRow({ kpi, checked, onChange, isLast, disabled, databases, kpiDbMap, onKpiDbChange, showDbPicker }) {
  const combos = parseTags(kpi.tags);
  const allTags = []; const seen = new Set();
  for (const combo of combos)
    for (const t of combo) {
      if (!t?.key || !t?.value) continue;
      const k = `${t.key}:::${t.value}`;
      if (!seen.has(k)) { seen.add(k); allTags.push(t); }
    }

  const assignedDbId = kpiDbMap[kpi.questionId] || null;

  return (
    <div style={{
      borderBottom: isLast ? "none" : "1px solid #f1f5f9",
      background: checked ? "#f0f9ff" : "transparent",
      transition: "background 0.1s"
    }}>
      <div
        onClick={() => { if (!disabled) onChange(!checked); }}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: disabled ? "default" : "pointer" }}
      >
        <Checkbox checked={checked} onChange={onChange} disabled={disabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: checked ? "#0c4a6e" : "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
            {kpi.title || "—"}
          </div>
          {allTags.length > 0 && (
            <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
              {allTags.slice(0, 4).map((t, i) => (
                <span key={i} style={{ fontSize: 10, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4, padding: "1px 6px" }}>{t.value}</span>
              ))}
              {allTags.length > 4 && <span style={{ fontSize: 10, color: "#94a3b8" }}>+{allTags.length - 4}</span>}
            </div>
          )}
        </div>

        {showDbPicker && (
          <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <DatabasePicker
              databases={databases}
              value={assignedDbId}
              onChange={id => onKpiDbChange(kpi.questionId, id)}
              compact
              disabled={disabled || !checked}
            />
          </div>
        )}

        {!showDbPicker && assignedDbId && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "#fffbeb", color: "#d97706", border: "1px solid #fcd34d", flexShrink: 0 }}>
            🗄 DB#{assignedDbId}
          </span>
        )}

        {checked && <span style={{ fontSize: 9, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>SELECTED</span>}
      </div>
    </div>
  );
}

// ─── Trie helpers ─────────────────────────────────────────────────────────────
function buildTrie(questions) {
  const root = { children: new Map(), kpiIds: new Set() };
  for (const q of questions) {
    const combos = parseTags(q.tags);
    for (const combo of combos)
      if (combo && combo.length) insertCombo(root, combo, q.questionId);
  }
  return root;
}

function insertCombo(node, combo, qId) {
  node.kpiIds.add(qId);
  if (!combo.length) return;
  const tag = combo[0];
  if (!tag?.key || !tag?.value) return;
  if (!node.children.has(tag.key)) node.children.set(tag.key, new Map());
  const km = node.children.get(tag.key);
  if (!km.has(tag.value)) km.set(tag.value, { children: new Map(), kpiIds: new Set() });
  insertCombo(km.get(tag.value), combo.slice(1), qId);
}

function traverseTrieMulti(trie, steps) {
  let nodes = [trie];
  for (const step of steps) {
    if (!step?.key || !step?.value) continue;
    const next = [];
    for (const node of nodes) {
      const km = node.children?.get(step.key);
      if (!km) continue;
      const c = km.get(step.value);
      if (c) next.push(c);
    }
    if (!next.length) return [];
    nodes = next;
  }
  return nodes;
}

function getAvailableKeysMulti(nodes, usedKeys) {
  const kvm = new Map();
  for (const node of nodes) {
    if (!node?.children) continue;
    for (const [key, vm] of node.children.entries()) {
      if (usedKeys.has(key)) continue;
      if (!kvm.has(key)) kvm.set(key, new Set());
      for (const v of vm.keys()) kvm.get(key).add(v);
    }
  }
  return Array.from(kvm.entries()).map(([key, values]) => ({ key, values: Array.from(values).sort() }));
}

// ─── Selected drill-down node ─────────────────────────────────────────────────
function SelectedDrillNode({ keyName, questions, pendingIds, onToggle, savedIds, disabled, databases, kpiDbMap, onKpiDbChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalPath, setInternalPath] = useState([]);

  const relevantQuestions = useMemo(() => questions.filter(q => {
    if (!pendingIds.has(q.questionId)) return false;
    return parseTags(q.tags).some(c => c.some(t => t?.key === keyName));
  }), [questions, pendingIds, keyName]);

  const allQuestionsForKey = useMemo(() => questions.filter(q =>
    parseTags(q.tags).some(c => c.some(t => t?.key === keyName))
  ), [questions, keyName]);

  const trie = useMemo(() => buildTrie(relevantQuestions), [relevantQuestions]);

  const currentKpis = useMemo(() => {
    if (internalPath.length === 0) return relevantQuestions;
    const nodes = traverseTrieMulti(trie, internalPath);
    const validIds = new Set();
    nodes.forEach(n => n?.kpiIds?.forEach(id => validIds.add(id)));
    return relevantQuestions.filter(q => validIds.has(q.questionId));
  }, [relevantQuestions, internalPath, trie]);

  const topValues = useMemo(() => {
    const vals = new Set();
    const km = trie.children?.get(keyName);
    if (km) for (const v of km.keys()) vals.add(v);
    return Array.from(vals).sort();
  }, [trie, keyName]);

  const getOptionCounts = useCallback((key, value, pathPrefix = []) => {
    const matching = relevantQuestions.filter(q => {
      const combos = parseTags(q.tags);
      return combos.some(combo => {
        let pos = 0;
        let pathMatched = true;
        for (const step of pathPrefix) {
          const found = combo.find(t => t?.key === step.key && t?.value === step.value);
          if (!found) {
            pathMatched = false;
            break;
          }
        }
        if (!pathMatched) return false;
        return combo.some(t => t?.key === key && t?.value === value);
      });
    });
    const totalCount = matching.length;
    const assignedCount = matching.filter(q => !!kpiDbMap[q.questionId]).length;
    return { assignedCount, totalCount };
  }, [relevantQuestions, kpiDbMap]);

  const pathLevelOptions = useMemo(() => {
    const options = [];
    for (let i = 1; i < internalPath.length; i++) {
      const pathUpTo = internalPath.slice(0, i);
      const nodes = traverseTrieMulti(trie, pathUpTo);
      const vals = new Set();
      for (const node of nodes) {
        const km = node.children?.get(internalPath[i].key);
        if (km) for (const v of km.keys()) vals.add(v);
      }
      options.push({ key: internalPath[i].key, values: Array.from(vals).sort() });
    }
    return options;
  }, [trie, internalPath]);

  const nextAvailableKeyData = useMemo(() => {
    if (internalPath.length === 0) return [];
    const nodes = traverseTrieMulti(trie, internalPath);
    return getAvailableKeysMulti(nodes, new Set([keyName, ...internalPath.map(p => p.key)]));
  }, [trie, internalPath, keyName]);

  const handleDropdownChange = (key, value, index) => {
    const newPath = [...internalPath.slice(0, index)];
    if (value) newPath.push({ key, value });
    setInternalPath(newPath);
  };

  const hasUnsaved = !disabled && relevantQuestions.some(q => !savedIds.has(q.questionId));
  const selectedCount = relevantQuestions.length;
  const assignedCount = useMemo(() => {
    return relevantQuestions.filter(q => !!kpiDbMap[q.questionId]).length;
  }, [relevantQuestions, kpiDbMap]);

  const assignedKpis = useMemo(() => {
    return currentKpis.filter(q => !!kpiDbMap[q.questionId]);
  }, [currentKpis, kpiDbMap]);

  return (
    <div style={{ borderBottom: "1px solid #f1f5f9" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 20px", cursor: "pointer", background: isOpen ? "#f8fafc" : "#fff", transition: "background 0.15s", userSelect: "none" }}
      >
        <div style={{ width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: isOpen ? "#3f88a5" : "#e2e8f0", transition: "all 0.2s", flexShrink: 0 }}>
          <span style={{ fontSize: 8, color: isOpen ? "#fff" : "#64748b", transform: isOpen ? "rotate(90deg)" : "none", display: "block", transition: "transform 0.2s" }}>▶</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#3f88a5", textTransform: "uppercase", letterSpacing: "0.8px" }}>{formatTagKey(keyName)}</span>
        {hasUnsaved && <span style={{ fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 4, padding: "1px 6px" }}>UNSAVED</span>}
        <div style={{ flex: 1 }} />
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
          background: assignedCount === selectedCount ? "#e0f2fe" : "#fef3c7",
          color: assignedCount === selectedCount ? "#0369a1" : "#d97706",
          border: `1px solid ${assignedCount === selectedCount ? "#bae6fd" : "#fcd34d"}`, whiteSpace: "nowrap"
        }}>
          {assignedCount}/{selectedCount}
        </span>
      </div>

      {isOpen && (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
          {topValues.length > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 8, minHeight: 24 }}>
                {internalPath.length > 0 && (
                  <button onClick={() => setInternalPath([])} style={{ fontSize: 10, color: "#ef4444", border: "1px solid #fecaca", background: "#fff5f5", borderRadius: 5, padding: "2px 8px", cursor: "pointer", fontWeight: 600 }}>✕ Reset</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>{formatTagKey(keyName)}</label>
                  <select
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: `1.5px solid ${internalPath[0]?.key === keyName ? "#93c5fd" : "#e2e8f0"}`, fontSize: 12, outline: "none", background: "#fff", color: "#374151", cursor: "pointer" }}
                    value={internalPath[0]?.key === keyName ? internalPath[0].value : ""}
                    onChange={e => handleDropdownChange(keyName, e.target.value, 0)}
                  >
                    <option value="">All {formatTagKey(keyName)}s</option>
                    {topValues.map(v => {
                      const { assignedCount, totalCount } = getOptionCounts(keyName, v, []);
                      return <option key={v} value={v}>{v} ({assignedCount}/{totalCount})</option>;
                    })}
                  </select>
                </div>

                {internalPath.length > 1 && pathLevelOptions.map((levelOpts, idx) => (
                  <div key={internalPath[idx + 1].key} style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {formatTagKey(internalPath[idx + 1].key)}
                    </label>
                    <select
                      style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid #93c5fd", fontSize: 12, outline: "none", background: "#fff", color: "#374151", cursor: "pointer" }}
                      value={internalPath[idx + 1].value}
                      onChange={e => handleDropdownChange(internalPath[idx + 1].key, e.target.value, idx + 1)}
                    >
                      <option value="">All {formatTagKey(internalPath[idx + 1].key)}s</option>
                      {levelOpts.values.map(v => {
                        const { assignedCount, totalCount } = getOptionCounts(levelOpts.key, v, internalPath.slice(0, idx + 1));
                        return (
                          <option key={v} value={v}>
                            {v} ({assignedCount}/{totalCount})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ))}

                {internalPath.length > 0 && nextAvailableKeyData.length > 0 && (
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "#3f88a5", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>{formatTagKey(nextAvailableKeyData[0].key)}</label>
                    <select
                      style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid #bae6fd", fontSize: 12, outline: "none", background: "#f0f9ff", color: "#0369a1", cursor: "pointer" }}
                      value=""
                      onChange={e => { const [k, v] = e.target.value.split(":::"); if (k && v) handleDropdownChange(k, v, internalPath.length); }}
                    >
                      <option value="">Select {formatTagKey(nextAvailableKeyData[0].key)}...</option>
                      {nextAvailableKeyData.map(group => (
                        <optgroup key={group.key} label={formatTagKey(group.key).toUpperCase()}>
                          {group.values.map(val => {
                            const { assignedCount, totalCount } = getOptionCounts(group.key, val, internalPath);
                            return (
                              <option key={`${group.key}-${val}`} value={`${group.key}:::${val}`}>
                                {val} ({assignedCount}/{totalCount})
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {internalPath.length > 0 && (
                <div style={{
                  marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 12px", background: "#f0f9ff",
                  borderRadius: 8, border: "1px solid #bae6fd",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, flexWrap: "wrap" }}>
                    {internalPath.map((step, i) => (
                      <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {i > 0 && <span style={{ color: "#bae6fd", fontSize: 13 }}>›</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8" }}>
                          {step.value}
                        </span>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: "#64748b" }}>assigned</span>
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: "2px 9px",
                      borderRadius: 10, background: "#0369a1", color: "#fff",
                    }}>
                      {currentKpis.filter(q => !!kpiDbMap[q.questionId]).length}
                    </span>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>/</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 9px",
                      borderRadius: 10, background: "#e0f2fe",
                      color: "#0369a1", border: "1px solid #bae6fd",
                    }}>
                      {currentKpis.length}
                    </span>
                    <span style={{ fontSize: 10, color: "#64748b" }}>selected</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ margin: "0 20px 16px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {assignedKpis.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No KPIs with assigned databases match this filter.</div>
            ) : (
              <>
                <div style={{ padding: "8px 14px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>KPI Title</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {databases.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#d97706" }}>Assigned DB</span>}
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{assignedKpis.length} item{assignedKpis.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                {assignedKpis.map((q, idx) => {
                  const isUnsaved = !savedIds.has(q.questionId);
                  const assignedDbId = kpiDbMap[q.questionId] || null;
                  return (
                    <div key={q.questionId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: idx === assignedKpis.length - 1 ? "none" : "1px solid #f8fafc" }}>
                      <Checkbox checked={pendingIds.has(q.questionId)} onChange={c => !disabled && onToggle(q.questionId, c)} disabled={disabled} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, color: "#1e293b", fontWeight: 450, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={q.title}>{q.title}</span>
                      </div>
                      {databases.length > 0 && (
                        <div onClick={e => e.stopPropagation()}>
                          <DatabasePicker
                            databases={databases}
                            value={assignedDbId}
                            onChange={id => onKpiDbChange(q.questionId, id)}
                            compact
                            disabled={disabled}
                          />
                        </div>
                      )}
                      {isUnsaved && !disabled && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>UNSAVED</span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Selected accordion ───────────────────────────────────────────────────────
function SelectedKpisAccordion({ questions, savedIds, pendingIds, onToggle, disabled, databases, kpiDbMap, onKpiDbChange }) {
  const [selectedSearch, setSelectedSearch] = useState("");

  const dbSelectedQuestions = useMemo(() => {
    return questions.filter(q => pendingIds.has(q.questionId) && !!kpiDbMap[q.questionId]);
  }, [questions, pendingIds, kpiDbMap]);

  const topLevelKeys = useMemo(() => {
    const keys = new Set();
    questions.filter(q => pendingIds.has(q.questionId)).forEach(q => {
      parseTags(q.tags).forEach(c => { if (c[0]?.key) keys.add(c[0].key); });
    });
    return Array.from(keys).sort();
  }, [questions, pendingIds]);

  const filteredKeys = useMemo(() => {
    if (!selectedSearch.trim()) return topLevelKeys;
    const q = selectedSearch.toLowerCase();
    return topLevelKeys.filter(key => {
      if (formatTagKey(key).toLowerCase().includes(q)) return true;
      return questions.some(kpi =>
        pendingIds.has(kpi.questionId) &&
        parseTags(kpi.tags).some(c => c.some(t => t?.key === key)) &&
        kpi.title?.toLowerCase().includes(q)
      );
    });
  }, [topLevelKeys, selectedSearch, questions, pendingIds]);

  if (pendingIds.size === 0) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>○</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>No KPIs selected</div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Switch to the All KPIs tab to add KPIs.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding: "10px 20px", background: "#f0f9ff", borderBottom: "1px solid #bae6fd", display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{dbSelectedQuestions.length}</span>
        <span style={{ fontSize: 13, color: "#64748b" }}>assigned out of</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{questions.filter(q => pendingIds.has(q.questionId)).length}</span>
        {Object.keys(kpiDbMap).length > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "#fef3c7", color: "#d97706", border: "1px solid #fcd34d" }}>
            🗄 {Object.values(kpiDbMap).filter(Boolean).length} DB assignments
          </span>
        )}
      </div>

      <div style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ position: "relative" }}>
          <span style={{
            position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
            fontSize: 12, color: "#94a3b8", pointerEvents: "none",
          }}>⌕</span>
          <input
            value={selectedSearch}
            onChange={e => setSelectedSearch(e.target.value)}
            placeholder="Search selected KPIs by tag or title…"
            style={{
              width: "100%", fontSize: 12, padding: "7px 28px 7px 28px",
              border: "1.5px solid #e2e8f0", borderRadius: 8,
              background: "#fff", color: "#1e293b", outline: "none",
            }}
            onFocus={e => e.target.style.borderColor = "#93c5fd"}
            onBlur={e => e.target.style.borderColor = "#e2e8f0"}
          />
          {selectedSearch && (
            <span
              onClick={() => setSelectedSearch("")}
              style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                fontSize: 14, color: "#94a3b8", cursor: "pointer",
              }}
            >×</span>
          )}
        </div>
      </div>

      {filteredKeys.length === 0 ? (
        <div style={{ padding: "24px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
          No results for "{selectedSearch}"
        </div>
      ) : (
        filteredKeys.map(key => (
          <SelectedDrillNode
            key={key} keyName={key} questions={questions} pendingIds={pendingIds}
            onToggle={onToggle} savedIds={savedIds} disabled={disabled}
            databases={databases} kpiDbMap={kpiDbMap} onKpiDbChange={onKpiDbChange}
          />
        ))
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function EmissionKPISettings() {
  const [canUpdate] = usePermission("ghg-databases.update", "GHG_SETUP");
  const [allQuestions, setAllQuestions] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [isEnabled, setIsEnabled] = useState(true);
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Databases & per-KPI DB map ────────────────────────────────────────────
  const [databases, setDatabases] = useState([]);
  const [kpiDbMap, setKpiDbMap] = useState({});
  const [savedKpiDbMap, setSavedKpiDbMap] = useState({});

  // ── Financial Year ─────────────────────────────────────────────────────────
  const fyData = useMemo(() => { try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; } }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);

  const isViewOnly = selectedFY?.id !== latestFY?.id;
  const isDisableEdit = isViewOnly || !canUpdate;

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const prevLeafKey = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [settingsRes, dbRes, kpiDbRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ghg/databases`, {}, {}, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}quantative-data/ghgdbkpimappings`, {}, { fyid: selectedFY.id }, "GET"),
      ]);

      const rawDbs = dbRes?.data?.dataBaseList || dbRes?.data?.data?.dataBaseList || [];
      setDatabases(Array.isArray(rawDbs) ? rawDbs.map(normaliseDb) : []);

      const serverKpiDbMap = kpiDbRes?.data?.data?.kpiDbMap || {};
      setKpiDbMap(serverKpiDbMap);
      setSavedKpiDbMap(serverKpiDbMap);

      const userId = JSON.parse(localStorage.getItem("user_temp_id"));
      const fwRes = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET");
      const frameworks = fwRes.data?.data || [];
      const qRes = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`, {},
        { financialYearId: selectedFY.id, frameworkIds: frameworks.map(f => f.id), fromSettings: false },
        "GET"
      );
      const questions = qRes.data?.data || [];

      const validQuestionIds = new Set(questions.map(q => q.questionId));
      const raw = settingsRes.data;
      setIsEnabled(raw?.is_enabled ?? true);
      const savedK = Array.isArray(raw?.kpiIds) ? raw.kpiIds.map(Number) : [];
      const ids = raw?.id
        ? new Set(savedK.filter(id => validQuestionIds.has(id)))
        : new Set(questions.filter(q => q.kpi_type?.includes("EMISSION")).map(q => q.questionId));

      const filteredQuestions = questions.filter(q => ids.has(q.questionId));
      setAllQuestions(filteredQuestions);

      setSavedIds(ids);
      setPendingIds(new Set(ids));
    } catch (e) {
      console.error(e);
      showToast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  useEffect(() => {
    setFilterSteps([]);
    setSearch("");
    setKpiView("list");
    prevLeafKey.current = null;
    fetchAll();
  }, [fetchAll]);

  const activeSteps = filterSteps.filter(s => s.values?.size);
  const dbAssignedCount = useMemo(() => {
    return allQuestions.filter(q => pendingIds.has(q.questionId) && !!kpiDbMap[q.questionId]).length;
  }, [allQuestions, pendingIds, kpiDbMap]);

  const nextEligible = useMemo(() => getNextEligibleFilters(allQuestions, filterSteps), [allQuestions, filterSteps]);
  const isLeaf = activeSteps.length > 0 && nextEligible.length === 0;

  const leafQuestions = useMemo(() => {
    if (!isLeaf) return [];
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [isLeaf, allQuestions, filterSteps]);

  const handleSelectFiltered = useCallback((steps) => {
    if (isDisableEdit) return;
    const active = steps.filter(s => s.values?.size);
    if (!active.length) return;
    const matching = allQuestions.filter(q => questionMatchesSteps(q, steps));
    if (!matching.length) return;
    setPendingIds(prev => {
      const next = new Set(prev);
      matching.forEach(q => next.add(q.questionId));
      return next;
    });
  }, [allQuestions, isDisableEdit]);

  const filteredQuestions = useMemo(() => {
    let qs = allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      qs = qs.filter(q => (q.title || "").toLowerCase().includes(s) || (q.moduleName || "").toLowerCase().includes(s));
    }
    return qs;
  }, [allQuestions, filterSteps, search]);

  const showKpiList = isLeaf || search.trim().length > 0;
  const visibleKpis = showKpiList ? filteredQuestions : [];

  useEffect(() => {
    if (isDisableEdit || !isLeaf || leafQuestions.length === 0) return;
    const leafKey = filterSteps.map(s => `${s.key}:${[...s.values].join(",")}`).join("|");
    if (prevLeafKey.current === leafKey) return;
    prevLeafKey.current = leafKey;
    setPendingIds(prev => { const next = new Set(prev); leafQuestions.forEach(q => next.add(q.questionId)); return next; });
  }, [isLeaf, leafQuestions, filterSteps, isDisableEdit]);

  const toggleKpi = (id, checked) => {
    if (isDisableEdit) return;
    setPendingIds(p => { const n = new Set(p); checked ? n.add(id) : n.delete(id); return n; });
    if (!checked) {
      setKpiDbMap(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleBulkDeselect = useCallback((idsToDeselect) => {
    if (isDisableEdit) return;
    setKpiDbMap(prev => {
      const next = { ...prev };
      idsToDeselect.forEach(id => {
        delete next[id];
      });
      return next;
    });
  }, [isDisableEdit]);

  const handleKpiDbChange = (kpiId, dbId) => {
    if (isDisableEdit) return;
    setKpiDbMap(prev => {
      const next = { ...prev };
      if (!dbId) delete next[kpiId];
      else next[kpiId] = dbId;
      return next;
    });
  };

  // ── Save — tags stripped from payload ─────────────────────────────────────
  const handleSave = async () => {
    if (isDisableEdit) return;
    setSaving(true);
    try {

      const allKpiIds = new Set([
        ...Object.keys(kpiDbMap).map(Number),
        ...Object.keys(savedKpiDbMap).map(Number),
      ]);

      const changed = [];
      for (const kpiId of allKpiIds) {
        const prev = savedKpiDbMap[kpiId] ?? null;
        const next = pendingIds.has(kpiId) ? (kpiDbMap[kpiId] ?? null) : null;
        if (prev !== next) changed.push({ kpiId, ghgDbId: next });
      }

      let dbRes;

      if (changed.length === 0) {
        // No DB mapping changes — nothing to do on this endpoint
        dbRes = { data: { success: true } };
      } else {
        // Multiple changes → POST bulk replace (only changed entries, including deletes as null)
        const mappings = changed.map(({ kpiId, ghgDbId }) => ({ kpiId, ghgDbId }));

        dbRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings/kpiDbMappings`,
          {},
          { fyid: selectedFY?.id, mappings },
          "POST"
        );
      }

      if (dbRes?.data?.success) {
        setSavedIds(new Set(pendingIds));
        setSavedKpiDbMap({ ...kpiDbMap });
        setSaved(true);
        setFilterSteps([]);
        setSearch("");
        prevLeafKey.current = null;
        showToast("Preferences saved!");
        setTimeout(() => setSaved(false), 2000);
      } else {
        showToast("Save failed — please try again", "error");
      }
    } catch (e) {
      console.error(e);
      showToast("Error saving", "error");
    } finally {
      setSaving(false);
    }
  };

  const hasPendingChanges = useMemo(() => {
    if (isDisableEdit) return false;
    if (pendingIds.size !== savedIds.size) return true;
    for (const id of pendingIds) if (!savedIds.has(id)) return true;
    const allKeys = new Set([...Object.keys(kpiDbMap).map(Number), ...Object.keys(savedKpiDbMap).map(Number)]);
    for (const k of allKeys) if (kpiDbMap[k] !== savedKpiDbMap[k]) return true;
    return false;
  }, [pendingIds, savedIds, kpiDbMap, savedKpiDbMap, isDisableEdit]);

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI preferences…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @keyframes ekpi-spin { to { transform: rotate(360deg) } }
        @keyframes ekpi-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
        button:focus-visible { outline: 2px solid #3f88a5; outline-offset: 2px; }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10, zIndex: 1000,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "ekpi-toast-in 0.25s ease", display: "flex", alignItems: "center", gap: 8
        }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>{toast.msg}
        </div>
      )}

      {/* Top Bar */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>GHG Database Selection</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {pendingIds.size} KPI{pendingIds.size !== 1 ? "s" : ""} selected
              {Object.values(kpiDbMap).filter(Boolean).length > 0 && (
                <span style={{ color: "#d97706" }}> · {Object.values(kpiDbMap).filter(Boolean).length} DB assignment{Object.values(kpiDbMap).filter(Boolean).length !== 1 ? "s" : ""}</span>
              )}
              {hasPendingChanges && <span style={{ color: "#d97706", fontWeight: 600 }}> · unsaved changes</span>}
              {(isViewOnly || !canUpdate) && <span style={{ color: "#b45309", fontWeight: 600 }}> · view only</span>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={val => {
                const found = fyData.find(f => String(f.id) === String(val));
                if (found) setSelectedFY(found);
              }}
              options={fyData.map(fy => ({
                value: fy.id,
                label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}`,
              }))}
              isAlert={selectedFY?.id !== latestFY?.id}
            />
          )}
          <PrimaryButton
            onClick={handleSave}
            disabled={saving || !hasPendingChanges || isDisableEdit}
          >
            {saving ? <><Spinner size={12} color="#fff" /> Saving…</> : saved ? "✓ Saved!" : "Save Preferences"}
          </PrimaryButton>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: 1040, margin: "0 auto" }}>

        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        <DbLegend databases={databases} kpiDbMap={kpiDbMap} pendingIds={pendingIds} />

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, background: "#e2e8f0", padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 20 }}>
          {[
            { key: "list", label: "All KPIs", count: allQuestions.length },
            { key: "selected", label: "Selected", count: dbAssignedCount }
          ].map(tab => (
            <button key={tab.key} onClick={() => setKpiView(tab.key)} style={{
              padding: "7px 20px", border: "none", borderRadius: 8,
              background: kpiView === tab.key ? "#fff" : "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: kpiView === tab.key ? "#0f172a" : "#64748b",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition: "all 0.15s"
            }}>
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key ? (tab.key === "selected" ? "#dbeafe" : "#f1f5f9") : "#d1d5db",
                color: kpiView === tab.key ? (tab.key === "selected" ? "#1d4ed8" : "#475569") : "#94a3b8"
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* All KPIs Tab */}
        {kpiView === "list" && (
          <>
            <div style={{ background: "#fff", padding: "16px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
                <input
                  value={search}
                  onChange={e => !isViewOnly && setSearch(e.target.value)}
                  readOnly={isViewOnly}
                  placeholder="Search by title or module…"
                  style={{
                    width: "100%", padding: "9px 12px 9px 32px", border: "1.5px solid #e2e8f0",
                    borderRadius: 9, fontSize: 13, outline: "none", color: "#1e293b",
                    transition: "border-color 0.15s",
                    background: isViewOnly ? "#f8fafc" : "#fff",
                    cursor: isViewOnly ? "default" : "text"
                  }}
                  onFocus={e => { if (!isViewOnly) e.target.style.borderColor = "#93c5fd"; }}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                />
              </div>
              <TagFilterBar
                allQuestions={allQuestions}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                pendingIds={pendingIds}
                disabled={isDisableEdit}
                onSelectFiltered={handleSelectFiltered}
              />
              <FilterDbAssignBar
                databases={databases}
                filterSteps={filterSteps}
                allQuestions={allQuestions}
                kpiDbMap={kpiDbMap}
                onKpiDbChange={handleKpiDbChange}
                disabled={isDisableEdit}
              />
            </div>

            {showKpiList ? (
              <>
                {search.trim().length > 0 && !isDisableEdit && databases.length > 0 && (
                  <BulkDbAssignBar
                    databases={databases}
                    visibleQuestions={visibleKpis}
                    pendingIds={pendingIds}
                    kpiDbMap={kpiDbMap}
                    onKpiDbChange={handleKpiDbChange}
                    disabled={isDisableEdit}
                  />
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 2px" }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {visibleKpis.length} KPI{visibleKpis.length !== 1 ? "s" : ""}
                    {isLeaf && !isDisableEdit ? " — all auto-selected, uncheck to exclude" : ""}
                    {isDisableEdit ? " — view only" : ""}
                    {search.trim() ? " (search results)" : ""}
                  </span>
                  <span style={{ fontSize: 11, color: "#0369a1", fontWeight: 600 }}>
                    {visibleKpis.filter(q => pendingIds.has(q.questionId)).length} / {visibleKpis.length} selected
                  </span>
                </div>

                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  {visibleKpis.length === 0 ? (
                    <div style={{ padding: "60px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>No KPIs match this filter.</div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: 580, overflowY: "auto" }}>
                      {visibleKpis.map((q, idx) => (
                        <KpiListRow
                          key={q.questionId}
                          kpi={q}
                          checked={pendingIds.has(q.questionId)}
                          onChange={c => toggleKpi(q.questionId, c)}
                          isLast={idx === visibleKpis.length - 1}
                          disabled={isDisableEdit}
                          databases={databases}
                          kpiDbMap={kpiDbMap}
                          onKpiDbChange={handleKpiDbChange}
                          showDbPicker={databases.length > 0}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "48px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                  {activeSteps.length === 0 ? "Select a filter above to get started" : "Continue selecting filters to view KPIs"}
                </div>
                <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                  KPIs appear once all filter levels are chosen, or search for a specific KPI above
                </div>
              </div>
            )}
          </>
        )}

        {/* Selected Tab */}
        {kpiView === "selected" && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <SelectedKpisAccordion
              questions={allQuestions}
              savedIds={savedIds}
              pendingIds={pendingIds}
              onToggle={toggleKpi}
              disabled={isDisableEdit}
              databases={databases}
              kpiDbMap={kpiDbMap}
              onKpiDbChange={handleKpiDbChange}
            />
          </div>
        )}

        {databases.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 14px", background: "#fff", border: "1px solid #f1f5f9", borderRadius: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>LEGEND:</span>
            <span style={{ fontSize: 10, color: "#d97706" }}>🗄 = GHG database assigned per KPI (stored individually)</span>
            <span style={{ fontSize: 10, color: "#0369a1" }}>Filter DB Dropdown = assigns to ALL KPIs matching selected filters at once</span>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>Use "Bulk assign" for search results</span>
          </div>
        )}
      </div>
    </div>
  );
}